import gsap from 'gsap';

/**
 * Splideのアクティブスライド連動型テキストアニメーション
 */
export class SplideTextAnimation {
  /**
   * @param {Object} splideInstance - 初期化済みのSplideインスタンス
   */
  constructor(splideInstance) {
    this.splide = splideInstance;
    this.DOM = {};
    // スライド内のアニメーション対象をすべて取得
    this.DOM.targets = this.splide.root.querySelectorAll('[data-char-split]');
    
    this.COL_DELAY = 0.3; // 段落ごとの遅延時間（秒）
    this.CHAR_STAGGER = 0.05; // 各文字の間隔（秒）
    this.DURATION = 0.3; // 各文字のフェードイン時間（秒）

    if (this.DOM.targets.length === 0) return;

    this.init();
  }

  init() {
    // 1. 準備段階：すべての対象テキストをあらかじめ1文字ずつに分解しておく
    this.DOM.targets.forEach((target) => {
      this._splitText(target);
    });

    // 2. スライドが切り替わって「アクティブ」になった瞬間のイベントを監視
    this.splide.on('active', (EventDetail) => {
      // EventDetail.slide に現在アクティブになったスライド（li要素）が入ってきます
      const activeSlide = EventDetail.slide;
      
      // アクティブスライドの中にあるアニメーション対象を探す
      const targetsInSlide = activeSlide.querySelectorAll('[data-char-split]');
      
      targetsInSlide.forEach((target) => {
        this._animate(target);
      });
    });

    // 3. スライドから外れた（非アクティブになった）ら、文字を元の非表示状態（opacity: 0）にリセットする
    this.splide.on('inactive', (EventDetail) => {
      const inactiveSlide = EventDetail.slide;
      const targetsInSlide = inactiveSlide.querySelectorAll('[data-char-split]');
      
      targetsInSlide.forEach((target) => {
        const chars = target.querySelectorAll('.char');
        gsap.killTweensOf(chars); // 動作中のアニメーションがあれば強制停止
        gsap.set(chars, { opacity: 0, y: 10 }); // 初期状態に戻す
      });
    });

    // mounted後に生成された場合、現在のアクティブスライドを再生
    this.playActive();
  }

  playActive() {
    const slides = this.splide.Components?.Elements?.slides;
    if (!slides) return;
    const activeSlide = slides[this.splide.index];
    if (!activeSlide) return;
    const targetsInSlide = activeSlide.querySelectorAll('[data-char-split]');
    targetsInSlide.forEach((target) => {
      this._animate(target);
    });
  }

  /**
   * テキストをあらかじめ分解してHTMLを再構築する（準備処理）
   */
  _splitText(target) {
    const htmlString = target.innerHTML;

    // <br> および <br class="..."> などのタグをキャプチャ（保持）しながら分割
    const parts = htmlString.split(/(<br\b[^>]*\/?>)/i);

    let resultHtml = '';

    parts.forEach((part) => {
      // <br ...> タグそのものの場合は、アニメーション対象（span）にせずそのまま出力
      if (/^<br\b[^>]*\/?>$/i.test(part.trim())) {
        resultHtml += part;
      } else {
        // テキスト部分のみ文字単位で <span class="char"> に分解
        // ※スペースなどの連続文字や文字コードを壊さないよう split('') を使用
        const chars = part.split('');
        const wrappedChars = chars.map((char) => {
          // 空白文字（スペースや改行コードなど）はそのまま返し、通常の文字だけspanで囲む
          if (char === ' ' || char === '\n' || char === '\r' || char === '\t') {
            return char;
          }
          return `<span class="char">${char}</span>`;
        }).join('');

        resultHtml += wrappedChars;
      }
    });

    target.innerHTML = resultHtml;

    // 最初はすべて非表示状態にしておく
    const chars = target.querySelectorAll('.char');
    gsap.set(chars, { opacity: 0, y: 10 });
  }
  /**
   * アクティブになった際のアニメーション実行
   */
  _animate(target) {
    const lines = target.innerHTML.split(/<br\s*\/?>/i);
    
    // 各行ごとの .char 要素を仕分ける
    const lineCharsArr = [];
    let currentCharIndex = 0;
    const allChars = Array.from(target.querySelectorAll('.char'));

    lines.forEach((line) => {
      // プレーンテキストに変換して文字数をカウント（空白等を除く）
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = line;
      const charCount = tempDiv.textContent.length;
      
      // この行に属するspan要素をスライスして配列に入れる
      const lineChars = allChars.slice(currentCharIndex, currentCharIndex + charCount);
      lineCharsArr.push(lineChars);
      
      currentCharIndex += charCount;
    });

    // GSAPタイムラインで実行
    const tl = gsap.timeline({
      defaults: {
        ease: 'power2.out',
        duration: this.DURATION
      }
    });

    lineCharsArr.forEach((charsInLine, index) => {
      tl.to(charsInLine, {
        opacity: 1,
        y: 0,
        stagger: this.CHAR_STAGGER,
      }, index * this.COL_DELAY); // 🌟2段目は1段目開始から0.2s後に開始
    });
  }
}
